import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {CatalogConfig, CatalogItem} from '../types'
import {isItemActive} from '../utils/catalogFilter'
import {isSortable, Sort} from '../utils/serverTable'

type Props = {
  config: CatalogConfig
  items: CatalogItem[]
  // When false (read-only catalog), the Actions column and its row buttons are hidden.
  writable: boolean
  // Server-side sort state + handler. Absent (client-side mode) → headers are plain, unsortable.
  sort?: Sort | null
  onSort?: (field: string) => void
  onEdit: (item: CatalogItem) => void
  onToggleActive: (item: CatalogItem) => void
}

// Generic list table. Columns come from the fields flagged showInTable, plus Status and (for
// writable catalogs) Actions. Row actions are Edit and Deactivate/Reactivate — never hard
// delete. In server-side mode, columns listed in config.serverSide.sortableFields render a
// clickable header that cycles asc → desc → default. Mirrors the plain
// <table className="admin-community__table"> markup across the admin surface.
export const CatalogAdminTable = ({
  config,
  items,
  writable,
  sort,
  onSort,
  onEdit,
  onToggleActive,
}: Props) => {
  const intl = useIntl()
  const columns = config.fields.filter((field) => field.showInTable)
  // Status column + Deactivate/Reactivate only exist for catalogs with a soft-delete field.
  const showStatus = Boolean(config.softDelete)

  return (
    <table className='admin-community__table'>
      <thead>
        <tr>
          {columns.map((field) => {
            const label = intl.formatMessage({id: field.labelId})
            const sortable = Boolean(onSort) && isSortable(config, field.name)
            if (!sortable) {
              return <th key={field.name}>{label}</th>
            }
            const active = sort?.field === field.name
            const indicator = !active ? '' : sort?.dir === 'asc' ? ' ▲' : ' ▼'
            return (
              <th
                key={field.name}
                aria-sort={!active ? 'none' : sort?.dir === 'asc' ? 'ascending' : 'descending'}
              >
                <button
                  type='button'
                  className='nb-link-underline-500'
                  onClick={() => onSort?.(field.name)}
                >
                  {label}
                  {indicator}
                </button>
              </th>
            )
          })}
          {showStatus && <th>{intl.formatMessage({id: 'CATALOG.COLUMN.STATUS'})}</th>}
          {writable && <th>{intl.formatMessage({id: 'CATALOG.COLUMN.ACTIONS'})}</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const active = isItemActive(item, config)
          return (
            <tr key={item.id}>
              {columns.map((field) => {
                const value = item[field.name]
                if (field.type === 'url' && value) {
                  return (
                    <td key={field.name}>
                      <a href={String(value)} target='_blank' rel='noreferrer noopener'>
                        {String(value)}
                      </a>
                    </td>
                  )
                }
                return <td key={field.name}>{value ? String(value) : '—'}</td>
              })}
              {showStatus && (
                <td>
                  <span
                    className={`badge ${active ? 'badge-light-success' : 'badge-light-danger'}`}
                  >
                    {intl.formatMessage({
                      id: active ? 'CATALOG.STATUS.ACTIVE' : 'CATALOG.STATUS.INACTIVE',
                    })}
                  </span>
                </td>
              )}
              {writable && (
                <td className='nb-catalog-actions-cell'>
                  <RowActionsMenu
                    activeStatus={active}
                    showStatus={showStatus}
                    onEdit={() => onEdit(item)}
                    onToggleActive={() => onToggleActive(item)}
                    editLabel={intl.formatMessage({id: 'CATALOG.ACTION.EDIT'})}
                    toggleLabel={intl.formatMessage({
                      id: active ? 'CATALOG.ACTION.DEACTIVATE' : 'CATALOG.ACTION.REACTIVATE',
                    })}
                  />
                </td>
              )}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// Single "⋯" dropdown replacing the two loose buttons; unifies the row-action look across
// every catalog admin screen. Closes on outside click and Escape.
type RowActionsProps = {
  activeStatus: boolean
  showStatus: boolean
  onEdit: () => void
  onToggleActive: () => void
  editLabel: string
  toggleLabel: string
}

const RowActionsMenu = ({
  showStatus,
  onEdit,
  onToggleActive,
  editLabel,
  toggleLabel,
}: RowActionsProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className='nb-catalog-actions' ref={ref}>
      <button
        type='button'
        className='nb-catalog-actions__trigger'
        aria-haspopup='menu'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open && (
        <div className='nb-catalog-actions__menu' role='menu'>
          <button
            type='button'
            role='menuitem'
            className='nb-catalog-actions__item'
            onClick={() => {
              setOpen(false)
              onEdit()
            }}
          >
            {editLabel}
          </button>
          {showStatus && (
            <button
              type='button'
              role='menuitem'
              className='nb-catalog-actions__item'
              onClick={() => {
                setOpen(false)
                onToggleActive()
              }}
            >
              {toggleLabel}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
